import { IFilterComp, IFilterParams, IDoesFilterPassParams } from "ag-grid-community";

interface StatusCheckboxFilterParams extends IFilterParams {
  values: string[];
  labels?: Record<string, string>;
  locales?: {
    placeholder?: string;
    apply_button?: string;
    reset_button?: string;
  };
}

interface FilterModel {
  filterType: "set";
  values: string[];
}

export class StatusCheckboxFilter implements IFilterComp {
  private params!: StatusCheckboxFilterParams;
  private container!: HTMLElement;
  private searchInput!: HTMLInputElement;
  private listEl!: HTMLElement;
  private selected = new Set<string>();
  private filterText = "";
  private hidePopup?: () => void;

  init(params: StatusCheckboxFilterParams): void {
    this.params = params;
    const locales = this.params.locales || {};
    this.container = document.createElement("div");
    this.container.className = "bg-white border border-gray-300 rounded p-2 min-w-[240px]";

    this.searchInput = document.createElement("input");
    this.searchInput.type = "text";
    this.searchInput.className = "w-full px-2 py-1.5 border border-gray-300 rounded text-sm box-border";
    this.searchInput.placeholder = locales.placeholder || "Search...";
    this.searchInput.addEventListener("input", () => {
      this.filterText = this.searchInput.value.toLowerCase();
      this.renderList();
    });

    const buttons = document.createElement("div");
    buttons.className = "flex justify-end gap-2 pt-2";

    const resetBtn = document.createElement("button");
    resetBtn.type = "button";
    resetBtn.className = "px-3 py-1.5 border border-gray-300 rounded bg-white hover:bg-gray-50 cursor-pointer text-sm";
    resetBtn.textContent = locales.reset_button || "Reset";
    resetBtn.addEventListener("click", () => {
      this.selected.clear();
      this.searchInput.value = "";
      this.filterText = "";
      this.renderList();
      this.params.filterChangedCallback();
      this.hidePopup?.();
    });

    const applyBtn = document.createElement("button");
    applyBtn.type = "button";
    applyBtn.className = "px-3 py-1.5 border border-gray-300 rounded bg-white hover:bg-gray-50 cursor-pointer text-sm";
    applyBtn.textContent = locales.apply_button || "Apply";
    applyBtn.addEventListener("click", () => {
      this.params.filterChangedCallback();
      this.hidePopup?.();
    });

    buttons.append(resetBtn, applyBtn);

    this.listEl = document.createElement("div");
    this.listEl.className = "mt-2 max-h-60 overflow-auto";

    this.container.append(this.searchInput, this.listEl, buttons);

    this.hidePopup = () => {
      requestAnimationFrame(() => {
        const filterMenu = this.container.closest(".ag-menu.ag-filter-menu");
        filterMenu?.remove();
      });
    };

    this.renderList();
  }

  getGui(): HTMLElement {
    return this.container;
  }

  afterGuiAttached(): void {
    this.searchInput.focus();
  }

  isFilterActive(): boolean {
    return this.selected.size > 0;
  }

  getModel(): FilterModel | null {
    if (!this.isFilterActive()) return null;
    return { filterType: "set", values: Array.from(this.selected) };
  }

  setModel(model: FilterModel | null): void {
    this.selected.clear();
    model?.values?.forEach((v) => this.selected.add(v));
    this.renderList();
  }

  doesFilterPass(_params: IDoesFilterPassParams): boolean {
    return true;
  }

  private renderList(): void {
    const values = Array.isArray(this.params.values) ? this.params.values : [];
    const labels = this.params.labels || {};
    const filtered = this.filterText ? values.filter((v) => v.toLowerCase().includes(this.filterText)) : values;

    this.listEl.innerHTML = "";
    filtered.forEach((value) => {
      const row = document.createElement("label");
      row.className = "flex items-center gap-2 py-1 text-sm cursor-pointer select-none";

      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.checked = this.selected.has(value);
      cb.addEventListener("change", () => {
        if (cb.checked) this.selected.add(value);
        else this.selected.delete(value);
      });

      const text = document.createElement("span");
      text.textContent = labels[value] || value;

      row.append(cb, text);
      this.listEl.appendChild(row);
    });
  }
}
